import { Component, OnInit, ChangeDetectorRef, Input, ChangeDetectionStrategy, EventEmitter, Output, ViewChild, ElementRef, Inject } from '@angular/core';
import { ActionEvent } from '@schoolbelle/common/table-resizer';
import { Table } from '@schoolbelle/common/table';
import { ExcelService } from '@schoolbelle/api/excel';
import { LoadingService } from '@schoolbelle/common/loading';
import { reduce, isEmpty, forEach, forEachRight, values } from 'lodash-es';
import { FormControl, FormGroup } from '@angular/forms';
import { MailMergeItem } from '@schoolbelle/api/letter';
import { Subscription } from 'rxjs';
import { MailmergeTableService, possible_search_type_list } from './mailmerge-table.service';
import { debounceTime, finalize } from 'rxjs/operators';
import { ngfSelect } from 'angular-file';
import { ToastrService } from 'ngx-toastr';
import { TranslationService } from '@schoolbelle/common/translation';
import { GroupMemberType } from '@schoolbelle/api/group-member';
import { reduceFormErrors } from '@schoolbelle/common/functions';
import { VirtualScrollerComponent } from 'ngx-virtual-scroller';
import { CountryCallingCode } from 'libphonenumber-js';
import { DEFAULT_COUNTRY_CALLING_CODE } from '@schoolbelle/common/phone-number';
// const default_country_dial_code = '82';

@Component({
  selector: 'app-mailmerge-table',
  templateUrl: './mailmerge-table.component.html',
  styleUrls: ['./mailmerge-table.component.scss'],
  changeDetection:ChangeDetectionStrategy.OnPush,

})
export class MailmergeTableComponent implements OnInit {
  @ViewChild(ngfSelect) ngfSelect:ngfSelect;
  @Input() table:Table;
  @Input() memberType:GroupMemberType;
  @Input() searchTypes:Array<'class'|'type'|'name'|'phone-number'> = ['class', 'name'];
  
  _mailmerge:MailMergeItem[];
  @Input() mailmerge:MailMergeItem[]; //getting values from parents components..
  @Output() mailmergeChange = new EventEmitter(); //tossing values from child component to parents components...
  @Input() selectedRowIndex;
  @Output() selectedRowIndexChange = new EventEmitter();
  @Input() height = '20vh';
  @Input() memberMatchRequired:boolean = true;
  @ViewChild(VirtualScrollerComponent) scroll:VirtualScrollerComponent;
  tableCtrl:FormControl;
  tableForm:FormGroup;
  subscription = new Subscription();
  constructor(
    private cdRef:ChangeDetectorRef,
    private excel:ExcelService,
    private loading:LoadingService,
    private mailmergeService:MailmergeTableService,
    private toastr:ToastrService,
    private elementRef: ElementRef,
    private translation: TranslationService,
    @Inject(DEFAULT_COUNTRY_CALLING_CODE) protected DEFAULT_COUNTRY_CALLING_CODE:CountryCallingCode,
  ) { 
  }
  ngOnInit() {
    /**
     * this must be here for calculating inial data.
     */
    forEach(this.elementRef.nativeElement.querySelectorAll("div[translation-text]"), el => this.translation.set(el.id, el.attributes["translation-text"].value));

    this.tableCtrl = new FormControl();
    this.tableForm = new FormGroup({});
    
    if (!this.table) { // expected value => undefined or null
      this.table = new Table();
    }

    if (this.mailmerge.length === 0) {
      this.mailmerge = [{group_id:0, scopes:'none', student_id:0, mm_data: {'0':this.translation.get('mailmerge-table-class'), '1':this.translation.get('mailmerge-table-name')}}];
    }

    // calculate inial data
    const data = this.mailmergeService.turnMailMergeListToTableArrayData(this.mailmerge, possible_search_type_list.map(type=>this.translation.get(`mailmerge-table-${type}`)));
    if (data) {
      this.loadTable(data.body, data.header);
    }

    this.subscription.add(
      this.tableForm.statusChanges
      .pipe(
        debounceTime(0),
      )
      .subscribe(()=>{
        const errors = reduceFormErrors(this.tableForm);
        const reduced = reduce(values(errors), (acc, errors:{[key:string]:any})=>{ return {...acc, ...errors}; }, {});
        this.tableCtrl.setErrors(isEmpty(reduced) ? null : reduced);
        this.cdRef.detectChanges()
      })
    );
    this.subscription.add(
      this.table.onContentUpdate.pipe(debounceTime(0))
      .subscribe(()=>{
        const list = this.mailmergeService.turnTableDataToMailMergeList(
          this.table, 
          {
            Grade:this.translation.get('mailmerge-table-grade') || 'Grade',
            Class:this.translation.get('mailmerge-table-class') || 'Class',
            PhoneNumber:this.translation.get('mailmerge-table-phone-number') || 'PhoneNumber',
            Num:this.translation.get('mailmerge-table-num') || 'Num',
            Name:this.translation.get('mailmerge-table-name') || 'Name',
          }, 
          this.memberMatchRequired,
        );

        this.mailmergeChange.next(list);
      })
    );
    this.selectRow(1);
  }

  ngOnDestroy () {
    this.subscription.unsubscribe();
  }
  /**
   * 
   * @param event 
   * => table + resize = mailmerge
   * => x,y can't added once ({x:click, y:undefined}, {x:undefined, y:click})
   * => 
   * 
   * 
   */
  onAction (event:ActionEvent) {
    switch (event.type) {
      //resize : concept of length
      //add, remove : concept of coords...
      case 'resize':
        if (typeof event.cordinations.x !== 'undefined') this.table.x = event.cordinations.x;
        else {
          this.table.y = event.cordinations.y;
          this.scroll.scrollToIndex(this.table.y - 1);

        }
      break

      case 'add':
        if (typeof event.cordinations.x !== 'undefined') {
          this.table.addColumn(event.cordinations.x);
        }  
        else {
          this.table.addRow(event.cordinations.y);
        }  
      break
      case 'remove':
        if (typeof event.cordinations.x !== 'undefined') {
          this.table.removeColumn(event.cordinations.x);
        }  
        else {
          this.table.removeRow(event.cordinations.y);
        }
      case 'click':
        if (typeof event.cordinations.y !== 'undefined') this.selectRow(event.cordinations.y);
      break  
    }
    this.cdRef.detectChanges();    
  }
  selectRow (index:number) {
    index  = index - 1;
    this.selectedRowIndex = index // minus header cell; 
    this.selectedRowIndexChange.next(index);    
  }  



  private loadTable (body:Array<Array<string>>, header:Array<string|number|Date> = []) {
    // order is important.
    // fill data fisrt
    // show new cells next

    let data = {};
    header.forEach((val, x)=>{
      data[`${x}x0`] = {val};
    });
    data = reduce(body, (acc, row, y)=>{
        const [member, cls] = this.mailmergeService.findMember(row[2], [row[1] as GroupMemberType], row[0]);
        return {...acc, ...reduce(row, (acc, val, x)=>{
          return {...acc, [`${x}x${y + 1}`]: 
            x === 0 ? {val:cls ? cls.group_name : val} : 
              x === 1 ? {val:member? member.member_type : val } :
                x === 2 ? {val:member? member.member_name : val } : {val}
          };
        }, acc)}
    }, data);
    
    //maxBy => return max value from array...
    const numOfHiddenCols = this.table.cols.filter(c=>c.hidden).length;
    this.table.x = (body.length ? header.length : 7) - numOfHiddenCols;  
    
    const numOfHiddenRows = this.table.rows.filter(c=>c.hidden).length;
    this.table.y = (body.length + 1 > 30 ? body.length + 1 : 30) - numOfHiddenRows;

    this.table.load(data);

    /**
     * hot fix : fix old header values 
     */
     possible_search_type_list.forEach((type, i)=>{
      // if (!this.searchTypes.includes(type)) return;
      const cell = this.table.rows[0].cells[i];
      if (!cell) return;
      if (cell.val !== this.translation.get(`mailmerge-table-${type}`)) cell.val = this.translation.get(`mailmerge-table-${type}`);
    })

    // hide unwanted columns
    possible_search_type_list.forEach((type, i)=>{
      if (!this.searchTypes.includes(type)) this.table.cols[i].hidden = true;
    });  
    // lock first 4 cells in the header
    this.table.rows[0].cells.slice(0, 4).forEach(cell=>cell.locked = true);
    



    this.cdRef.detectChanges();

  }
  /**
   * ruler cells for the FIXED columns in the top ruler will not be interactive.
   * @param x 
   * @param y 
   * 
   */
  disableIndexCellFn = (x:number, y:number) => {
    if (y === 0) return true; // disable manipulation of the header row
    else if (x < this.searchTypes.length) return true; // disable manipulation of the search-match columns 
    else return false;
  }
  /**
   * first ruller cell in the left ruler will be painted gray.
   * @param x 
   * @param y 
   * @returns 
   */
  classIndexCellFn = (x:number, y:number) => {
    if (y === 0) return 'table-secondary';
    return '';
  }



  onExcelUpload (file:File) {
    const subscription = this.excel.read(file)
    .pipe(
      finalize(()=>this.refreshFileInput())
    )
    .subscribe(data=>{
      /* array data */
      let header = data[0];
      header = header.map(v=>String(v));

      //slice(1) = slice array from front...
      const data_without_header = data.slice(1);
      /* trim data (remove empty row) */
      let data_trimmed = this.excel.trim(data_without_header);
      data_trimmed = data_trimmed.map(r=>r.map(c=>String(c).trim()));
      

      header.splice(0, this.searchTypes.length, 
        this.translation.get('mailmerge-table-class'),
        this.translation.get('mailmerge-table-type'),
        this.translation.get('mailmerge-table-name'),
        this.translation.get('mailmerge-table-phone-number')
      );
      data_trimmed.forEach(row=>{
        possible_search_type_list.map((type, index)=>{
          const exists:boolean = this.searchTypes.indexOf(type as any) !== -1;
          if (!exists) row.splice(index, 0, '');
        }) 
      });

      data_trimmed = data_trimmed.map((row, y)=>{
        return row.map((cell, x)=> {
          if (header[x] === this.translation.get('mailmerge-table-phone-number')) {
            const phone_number = cell && !(<string>cell.toString()).startsWith('+') ? '+' + this.DEFAULT_COUNTRY_CALLING_CODE + (<string>cell.toString()).replace(/\D/g, '').replace(/^0/, '') : cell; 
            return phone_number;
          }  
          else if (header[x] === this.translation.get('mailmerge-table-type')) {
            return this.memberType;
          }
          else return cell;  
        });
      });

      /* populate table */
      this.loadTable(data_trimmed, header);
    },e=>{
      this.toastr.error(e.error || e.message);
    })
    this.loading.open({message:this.translation.get('mailmerge-table-reading'), busy:subscription});
  }
  private refreshFileInput () {
    this.ngfSelect.files = [];
    const input = this.ngfSelect.element.nativeElement.querySelector('input');    
    if (input) input.value = null;
  }
  downloadAsExcel () {
    const table = this.table;
    const data = [
      [
        ...this.searchTypes.map(type=>this.translation.get(`mailmerge-table-${type}`)),
        ...table.rows[0].cells.slice(possible_search_type_list.length).map(cell=>cell.val)
      ]  
    ]
    .concat(table.rows.slice(1).map((row, cord_y)=>{
      let cell_values = row.cells.map((cell, cord_x)=>{
        let val = cell.val;
        return val;
      });
      forEachRight(possible_search_type_list, (type, index)=>{
        const exists:boolean = this.searchTypes.indexOf(type as any) !== -1;
        if (!exists) cell_values.splice(index, 1);
      }) 
      return cell_values;
    }));
    const ob = this.excel.write({'sheet1':data}, this.translation.get(`mailmerge-table-mailmerge`));
    this.loading.open({message:this.translation.get('mailmerge-table-downloading'), busy:ob.subscribe()});
  }   
  downloadTemplate () {
    const redHeaderCell = {bold:true, alignment:{horizontal:'center'}, fgColor: { rgb: 0xFF9999 }, bottom:{style:'thin'}}    
    const blueHeaderCell = {bold:true, alignment:{horizontal:'center'}, fgColor: { rgb: 0x94a9d7 }, bottom:{style:'thin'}}    
    const yellowCell = {bold:false, alignment:{horizontal:'center'}, fgColor: { rgb: 0xFFFFCC }, bottom:{style:'thin'}}    
    const cols = this.searchTypes.map(()=>{ return {width:10}  as any; }).concat({width:10}, {width:10}, {width:100});
    const table = [
      (
        this.searchTypes.map(type=>{ 
          return {v:this.translation.get(`mailmerge-table-${type}`), s:redHeaderCell}; 
        }) as any[]
      )
      .concat({v:this.translation.get('mailmerge-table-math'),s:blueHeaderCell}, {v:this.translation.get('mailmerge-table-science'), s:blueHeaderCell}, {v:this.translation.get('mailmerge-table-template-yellow-row-1'), s:yellowCell}),
      ...[1,2,3].map(i=>{
        return (
          this.searchTypes.map(type=>{ 
            return {v:this.translation.get(`mailmerge-table-${type.replace(/^[^_]_/, '')}${i}`), s:redHeaderCell}; 
          }) as any[]
        ).concat(100 - (i - 1)* 10 , 80 + ( i - 1) * 10 ,{v:this.translation.get(`mailmerge-table-template-yellow-row-${i + 1}`), s:yellowCell});
      }),
      
    ];
    const ob = this.excel.write({'sheet1':{table, cols}}, this.translation.get(`mailmerge-table-template`));
    this.loading.open({message:this.translation.get('mailmerge-table-downloading'), busy:ob.subscribe()});
  }   
  ngAfterViewInit () {
  }


  trackById (index:number, item:any) {
    return item.id;
  }
}